import React, { useState, useEffect } from "react";
import { ComponentConfig } from "@measured/puck";
import { useRootProps } from "../context";
import { IOrganizationProps } from "../../../types/Organization";
import { ISmallEventProps } from "../../../types/Event";
import { booleanField } from "../helpers";

const colorField = (label: string) => ({
  type: "custom" as const,
  label,
  render: ({
    value,
    onChange,
  }: {
    value?: string;
    onChange?: (val: string) => void;
  }) => (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input
        type="color"
        value={value || "#17265a"}
        onChange={(e) => onChange?.(e.target.value)}
        style={{
          width: 44,
          height: 32,
          padding: 0,
          border: "1px solid #e5e7eb",
          borderRadius: 6,
        }}
      />
      <input
        type="text"
        value={value || ""}
        placeholder="#17265a"
        onChange={(e) => onChange?.(e.target.value)}
        style={{
          flex: 1,
          padding: "8px 10px",
          borderRadius: 6,
          border: "1px solid #e5e7eb",
          fontFamily: "monospace",
        }}
      />
    </div>
  ),
});

// Navbar Section - Fixed section with customizable styling
export const NavbarSection: ComponentConfig<{
  logoUrl: string;
  brandText: string;
  backgroundColor: string;
  textColor: string;
  sticky: boolean;
  links: { label: string; href: string; newTab: boolean }[];
  loginButtonText: string;
  loginButtonColor: string;
  loginButtonTextColor: string;
  showLoginButton: boolean;
}> = {
  fields: {
    logoUrl: { type: "text", label: "Logo URL" },
    brandText: { type: "text", label: "Brand Text" },
    backgroundColor: colorField("Background Color"),
    textColor: colorField("Text Color"),
    sticky: { ...booleanField, label: "Sticky Navbar" },
    links: {
      type: "array",
      label: "Navigation Links",
      arrayFields: {
        label: { type: "text", label: "Label" },
        href: { type: "text", label: "Link URL" },
        newTab: { ...booleanField, label: "Open in New Tab" },
      },
    } as any,
    loginButtonText: { type: "text", label: "Login Button Text" },
    loginButtonColor: colorField("Login Button Color"),
    loginButtonTextColor: colorField("Login Button Text Color"),
    showLoginButton: { ...booleanField, label: "Show Login Button" },
  },
  defaultProps: {
    logoUrl: "",
    brandText: "Organization",
    backgroundColor: "#ffffff",
    textColor: "#000000",
    sticky: true,
    links: [
      { label: "Home", href: "#", newTab: false },
      { label: "About", href: "#about", newTab: false },
      { label: "Events", href: "#events", newTab: false },
      { label: "Contact", href: "#contact", newTab: false },
    ],
    loginButtonText: "Login",
    loginButtonColor: "#17265a",
    loginButtonTextColor: "#ffffff",
    showLoginButton: true,
  },
  render: (props: any) => {
    const NavbarSectionComponent: React.FC = () => {
      const {
        logoUrl,
        brandText,
        backgroundColor,
        textColor,
        sticky,
        links = [],
        loginButtonText,
        loginButtonColor,
        loginButtonTextColor,
        showLoginButton,
      } = props;
      const { organization } = useRootProps();
      const [isMobile, setIsMobile] = useState(false);
      const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

      useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
      }, []);

      const resolvedLogo =
        logoUrl || (organization as IOrganizationProps)?.logo || "";
      const resolvedBrandText =
        brandText ||
        (organization as IOrganizationProps)?.name ||
        "Organization";

      return (
        <nav
          style={{
            position: sticky ? "sticky" : "relative",
            top: 0,
            zIndex: 1000,
            width: "100%",
            backgroundColor,
            color: textColor,
            borderBottom: `1px solid ${textColor}20`,
            boxShadow: sticky ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
          }}
        >
          <div
            style={{
              maxWidth: "1280px",
              margin: "0 auto",
              padding: "16px 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {resolvedLogo && (
                <img
                  src={resolvedLogo}
                  alt={resolvedBrandText}
                  style={{
                    height: "40px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              )}
              <span
                style={{
                  fontWeight: 700,
                  fontSize: "20px",
                  color: textColor,
                }}
              >
                {resolvedBrandText}
              </span>
            </div>

            {!isMobile && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flex: 1,
                  justifyContent: "flex-end",
                  marginRight: 16,
                }}
              >
                {links.map((link: any, idx: number) => (
                  <a
                    key={idx}
                    href={link.href || "#"}
                    target={link.newTab ? "_blank" : "_self"}
                    rel={link.newTab ? "noopener noreferrer" : undefined}
                    style={{
                      color: textColor,
                      textDecoration: "none",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      fontWeight: 500,
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${textColor}15`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {showLoginButton && !isMobile && (
                <a
                  href="/login"
                  style={{
                    padding: "10px 20px",
                    backgroundColor: loginButtonColor,
                    color: loginButtonTextColor,
                    borderRadius: "8px",
                    textDecoration: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {loginButtonText}
                </a>
              )}
              {isMobile && (
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  style={{
                    background: "none",
                    border: "none",
                    color: textColor,
                    fontSize: "24px",
                    cursor: "pointer",
                    padding: "8px",
                  }}
                >
                  {mobileMenuOpen ? "✕" : "☰"}
                </button>
              )}
            </div>
          </div>

          {isMobile && mobileMenuOpen && (
            <div
              style={{
                borderTop: `1px solid ${textColor}20`,
                padding: "16px 24px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                backgroundColor,
              }}
            >
              {links.map((link: any, idx: number) => (
                <a
                  key={idx}
                  href={link.href || "#"}
                  target={link.newTab ? "_blank" : "_self"}
                  rel={link.newTab ? "noopener noreferrer" : undefined}
                  style={{
                    color: textColor,
                    textDecoration: "none",
                    padding: "12px 16px",
                    borderRadius: "6px",
                    fontWeight: 500,
                  }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              {showLoginButton && (
                <a
                  href="/login"
                  style={{
                    padding: "12px 20px",
                    backgroundColor: loginButtonColor,
                    color: loginButtonTextColor,
                    borderRadius: "8px",
                    textDecoration: "none",
                    fontWeight: 600,
                    textAlign: "center",
                    marginTop: 8,
                  }}
                >
                  {loginButtonText}
                </a>
              )}
            </div>
          )}
        </nav>
      );
    };
    return <NavbarSectionComponent />;
  },
};

// Footer Section - Fixed section with customizable content
export const FooterSection: ComponentConfig<{
  backgroundColor: string;
  textColor: string;
  copyrightText: string;
  showSocialLinks: boolean;
  socialLinks: { platform: string; url: string }[];
  columns: { title: string; links: { label: string; href: string }[] }[];
  paddingY: number;
}> = {
  fields: {
    backgroundColor: colorField("Background Color"),
    textColor: colorField("Text Color"),
    copyrightText: { type: "text", label: "Copyright Text" },
    showSocialLinks: { ...booleanField, label: "Show Social Links" },
    socialLinks: {
      type: "array",
      label: "Social Links",
      arrayFields: {
        platform: {
          type: "select",
          label: "Platform",
          options: [
            { label: "Facebook", value: "Facebook" },
            { label: "Twitter/X", value: "Twitter" },
            { label: "Instagram", value: "Instagram" },
            { label: "LinkedIn", value: "LinkedIn" },
            { label: "YouTube", value: "YouTube" },
          ],
        },
        url: { type: "text", label: "URL" },
      },
    } as any,
    columns: {
      type: "array",
      label: "Footer Columns",
      arrayFields: {
        title: { type: "text", label: "Column Title" },
        links: {
          type: "array",
          label: "Links",
          arrayFields: {
            label: { type: "text", label: "Link Label" },
            href: { type: "text", label: "Link URL" },
          },
        } as any,
      },
    } as any,
    paddingY: { type: "number", label: "Vertical Padding", min: 20, max: 120 },
  },
  defaultProps: {
    backgroundColor: "#1a1a1a",
    textColor: "#ffffff",
    copyrightText: "© 2024 Organization. All rights reserved.",
    showSocialLinks: true,
    socialLinks: [],
    columns: [
      {
        title: "Company",
        links: [
          { label: "About", href: "#about" },
          { label: "Contact", href: "#contact" },
        ],
      },
    ],
    paddingY: 60,
  },
  render: (props: any) => {
    const FooterSectionComponent: React.FC = () => {
      const {
        backgroundColor,
        textColor,
        copyrightText,
        showSocialLinks,
        socialLinks = [],
        columns = [],
        paddingY,
      } = props;
      const { organization } = useRootProps();

      // Get social links from organization if not provided
      const org = organization as IOrganizationProps;
      const resolvedSocialLinks =
        socialLinks.length > 0
          ? socialLinks
          : ([
              org?.facebook_link && {
                platform: "Facebook",
                url: org.facebook_link,
              },
              org?.instagram_link && {
                platform: "Instagram",
                url: org.instagram_link,
              },
              org?.linkedin_link && {
                platform: "LinkedIn",
                url: org.linkedin_link,
              },
              org?.x_link && { platform: "Twitter", url: org.x_link },
              org?.youtube_link && {
                platform: "YouTube",
                url: org.youtube_link,
              },
            ].filter(Boolean) as { platform: string; url: string }[]);

      return (
        <footer
          style={{
            backgroundColor,
            color: textColor,
            padding: `${paddingY}px 24px`,
            width: "100%",
          }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "32px",
                marginBottom: "32px",
              }}
            >
              {columns.map((column: any, idx: number) => (
                <div key={idx}>
                  {column.title && (
                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        marginBottom: "16px",
                        color: textColor,
                      }}
                    >
                      {column.title}
                    </h3>
                  )}
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {(column.links || []).map((link: any, linkIdx: number) => (
                      <li key={linkIdx}>
                        <a
                          href={link.href || "#"}
                          style={{
                            color: textColor,
                            textDecoration: "none",
                            opacity: 0.8,
                            fontSize: "14px",
                            transition: "opacity 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = "1";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = "0.8";
                          }}
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {showSocialLinks && resolvedSocialLinks.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginBottom: "32px",
                  flexWrap: "wrap",
                }}
              >
                {resolvedSocialLinks.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: textColor,
                      textDecoration: "none",
                      padding: "8px 16px",
                      border: `1px solid ${textColor}40`,
                      borderRadius: "6px",
                      fontSize: "14px",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${textColor}20`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    {link.platform}
                  </a>
                ))}
              </div>
            )}

            <div
              style={{
                borderTop: `1px solid ${textColor}20`,
                paddingTop: "24px",
                textAlign: "center",
                fontSize: "14px",
                opacity: 0.7,
              }}
            >
              {copyrightText}
            </div>
          </div>
        </footer>
      );
    };
    return <FooterSectionComponent />;
  },
};

// Events Section - Fixed section displaying events
export const EventsSection: ComponentConfig<{
  heading: string;
  subheading: string;
  layout: "grid" | "list";
  itemsPerRow: number;
  showImage: boolean;
  showDate: boolean;
  showLocation: boolean;
  showCategory: boolean;
  limit: number;
  backgroundColor: string;
  textColor: string;
  paddingY: number;
}> = {
  fields: {
    heading: { type: "text", label: "Section Heading" },
    subheading: { type: "textarea", label: "Section Subheading" },
    layout: {
      type: "select",
      label: "Layout",
      options: [
        { label: "Grid", value: "grid" },
        { label: "List", value: "list" },
      ],
    },
    itemsPerRow: {
      type: "number",
      label: "Items Per Row (Grid)",
      min: 1,
      max: 4,
    },
    showImage: { ...booleanField, label: "Show Event Image" },
    showDate: { ...booleanField, label: "Show Date" },
    showLocation: { ...booleanField, label: "Show Location" },
    showCategory: { ...booleanField, label: "Show Category" },
    limit: { type: "number", label: "Max Events to Show", min: 1, max: 50 },
    backgroundColor: colorField("Background Color"),
    textColor: colorField("Text Color"),
    paddingY: { type: "number", label: "Vertical Padding", min: 40, max: 120 },
  },
  defaultProps: {
    heading: "Upcoming Events",
    subheading: "Join us for our exciting events",
    layout: "grid",
    itemsPerRow: 3,
    showImage: true,
    showDate: true,
    showLocation: true,
    showCategory: true,
    limit: 6,
    backgroundColor: "#ffffff",
    textColor: "#000000",
    paddingY: 80,
  },
  render: (props: any) => {
    const EventsSectionComponent: React.FC = () => {
      const {
        heading,
        subheading,
        layout,
        itemsPerRow,
        showImage,
        showDate,
        showLocation,
        showCategory,
        limit,
        backgroundColor,
        textColor,
        paddingY,
      } = props;
      const { events = [] } = useRootProps();
      const displayEvents = (events as ISmallEventProps[]).slice(0, limit);

      return (
        <section
          id="events"
          style={{
            backgroundColor,
            color: textColor,
            padding: `${paddingY}px 24px`,
          }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            {(heading || subheading) && (
              <div style={{ marginBottom: "48px", textAlign: "center" }}>
                {heading && (
                  <h2
                    style={{
                      fontSize: "clamp(28px, 5vw, 42px)",
                      fontWeight: 700,
                      marginBottom: "16px",
                      color: textColor,
                    }}
                  >
                    {heading}
                  </h2>
                )}
                {subheading && (
                  <p
                    style={{
                      fontSize: "18px",
                      opacity: 0.8,
                      maxWidth: "600px",
                      margin: "0 auto",
                    }}
                  >
                    {subheading}
                  </p>
                )}
              </div>
            )}

            {displayEvents.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  opacity: 0.6,
                }}
              >
                No upcoming events at this time.
              </div>
            ) : layout === "list" ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                }}
              >
                {displayEvents.map((event) => (
                  <a
                    key={event.id}
                    href={`/events/${event.id}`}
                    style={{
                      display: "flex",
                      gap: "24px",
                      border: `1px solid ${textColor}20`,
                      borderRadius: "12px",
                      padding: "20px",
                      textDecoration: "none",
                      color: textColor,
                      transition: "transform 0.2s, box-shadow 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 24px rgba(0,0,0,0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    {showImage && event.cover_image && (
                      <img
                        src={event.cover_image}
                        alt={event.name}
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: 600,
                          marginBottom: "8px",
                          color: textColor,
                        }}
                      >
                        {event.name}
                      </h3>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "16px",
                          fontSize: "14px",
                          opacity: 0.8,
                        }}
                      >
                        {showDate && (
                          <span>
                            {new Date(event.date).toLocaleDateString()}
                          </span>
                        )}
                        {showLocation && (
                          <span>
                            {event.type === "online"
                              ? "Online"
                              : event.type === "hybrid"
                              ? "Hybrid"
                              : "In Person"}
                          </span>
                        )}
                        {showCategory && event.category && (
                          <span>{event.category}</span>
                        )}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(auto-fit, minmax(${
                    100 / Math.min(itemsPerRow, 4)
                  }%, 1fr))`,
                  gap: "24px",
                }}
              >
                {displayEvents.map((event) => (
                  <a
                    key={event.id}
                    href={`/events/${event.id}`}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      border: `1px solid ${textColor}20`,
                      borderRadius: "12px",
                      overflow: "hidden",
                      textDecoration: "none",
                      color: textColor,
                      transition: "transform 0.2s, box-shadow 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 32px rgba(0,0,0,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    {showImage && event.cover_image && (
                      <img
                        src={event.cover_image}
                        alt={event.name}
                        style={{
                          width: "100%",
                          height: "200px",
                          objectFit: "cover",
                        }}
                      />
                    )}
                    <div style={{ padding: "20px" }}>
                      <h3
                        style={{
                          fontSize: "18px",
                          fontWeight: 600,
                          marginBottom: "12px",
                          color: textColor,
                        }}
                      >
                        {event.name}
                      </h3>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          fontSize: "14px",
                          opacity: 0.8,
                        }}
                      >
                        {showDate && (
                          <div>{new Date(event.date).toLocaleDateString()}</div>
                        )}
                        {showLocation && (
                          <div>
                            {event.type === "online"
                              ? "Online"
                              : event.type === "hybrid"
                              ? "Hybrid"
                              : "In Person"}
                          </div>
                        )}
                        {showCategory && event.category && (
                          <div>{event.category}</div>
                        )}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </section>
      );
    };
    return <EventsSectionComponent />;
  },
};

// About Us Section - Fixed section with customizable content blocks
export const AboutUsSection: ComponentConfig<{
  heading: string;
  subheading: string;
  backgroundColor: string;
  textColor: string;
  paddingY: number;
  layout: "single-column" | "two-column" | "three-column";
  contentBlocks: {
    title: string;
    content: string;
    icon?: string;
  }[];
}> = {
  fields: {
    heading: { type: "text", label: "Section Heading" },
    subheading: { type: "textarea", label: "Section Subheading" },
    backgroundColor: colorField("Background Color"),
    textColor: colorField("Text Color"),
    paddingY: { type: "number", label: "Vertical Padding", min: 40, max: 120 },
    layout: {
      type: "select",
      label: "Content Layout",
      options: [
        { label: "Single Column", value: "single-column" },
        { label: "Two Columns", value: "two-column" },
        { label: "Three Columns", value: "three-column" },
      ],
    },
    contentBlocks: {
      type: "array",
      label: "Content Blocks",
      arrayFields: {
        title: { type: "text", label: "Block Title" },
        content: { type: "textarea", label: "Block Content" },
        icon: { type: "text", label: "Icon/Emoji (optional)" },
      },
    } as any,
  },
  defaultProps: {
    heading: "About Us",
    subheading: "Learn more about our organization",
    backgroundColor: "#f8f9fa",
    textColor: "#000000",
    paddingY: 80,
    layout: "two-column",
    contentBlocks: [
      {
        title: "Our Mission",
        content: "To bring people together through amazing events.",
        icon: "🎯",
      },
      {
        title: "Our Vision",
        content: "Creating memorable experiences for everyone.",
        icon: "🌟",
      },
    ],
  },
  render: (props: any) => {
    const AboutUsSectionComponent: React.FC = () => {
      const {
        heading,
        subheading,
        backgroundColor,
        textColor,
        paddingY,
        layout,
        contentBlocks = [],
      } = props;
      const { organization } = useRootProps();
      const org = organization as IOrganizationProps;

      // Use organization data if content blocks are empty
      const blocks =
        contentBlocks.length > 0
          ? contentBlocks
          : [
              org?.about?.mission && {
                title: "Mission",
                content: org.about.mission,
                icon: "🎯",
              },
              org?.about?.vision && {
                title: "Vision",
                content: org.about.vision,
                icon: "🌟",
              },
              org?.about?.values && {
                title: "Values",
                content: org.about.values,
                icon: "💎",
              },
            ].filter(Boolean);

      const gridCols =
        layout === "three-column" ? 3 : layout === "two-column" ? 2 : 1;

      return (
        <section
          id="about"
          style={{
            backgroundColor,
            color: textColor,
            padding: `${paddingY}px 24px`,
          }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            {(heading || subheading) && (
              <div style={{ marginBottom: "48px", textAlign: "center" }}>
                {heading && (
                  <h2
                    style={{
                      fontSize: "clamp(28px, 5vw, 42px)",
                      fontWeight: 700,
                      marginBottom: "16px",
                      color: textColor,
                    }}
                  >
                    {heading}
                  </h2>
                )}
                {subheading && (
                  <p
                    style={{
                      fontSize: "18px",
                      opacity: 0.8,
                      maxWidth: "600px",
                      margin: "0 auto",
                    }}
                  >
                    {subheading}
                  </p>
                )}
              </div>
            )}

            {blocks.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                  gap: "32px",
                }}
              >
                {blocks.map((block: any, idx: number) => (
                  <div
                    key={idx}
                    style={{
                      padding: "32px",
                      backgroundColor:
                        backgroundColor === "#f8f9fa"
                          ? "#ffffff"
                          : `${textColor}08`,
                      borderRadius: "12px",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    }}
                  >
                    {block.icon && (
                      <div
                        style={{
                          fontSize: "32px",
                          marginBottom: "16px",
                        }}
                      >
                        {block.icon}
                      </div>
                    )}
                    {block.title && (
                      <h3
                        style={{
                          fontSize: "24px",
                          fontWeight: 700,
                          marginBottom: "12px",
                          color: textColor,
                        }}
                      >
                        {block.title}
                      </h3>
                    )}
                    {block.content && (
                      <p
                        style={{
                          fontSize: "16px",
                          lineHeight: 1.6,
                          opacity: 0.9,
                          color: textColor,
                          margin: 0,
                        }}
                      >
                        {block.content}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      );
    };
    return <AboutUsSectionComponent />;
  },
};

// Newsletter Section - Fixed section with subscription form
export const NewsletterSection: ComponentConfig<{
  heading: string;
  subheading: string;
  buttonText: string;
  placeholderText: string;
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
  buttonTextColor: string;
  paddingY: number;
  apiEndpoint?: string;
}> = {
  fields: {
    heading: { type: "text", label: "Section Heading" },
    subheading: { type: "textarea", label: "Section Subheading" },
    buttonText: { type: "text", label: "Subscribe Button Text" },
    placeholderText: { type: "text", label: "Email Placeholder" },
    backgroundColor: colorField("Background Color"),
    textColor: colorField("Text Color"),
    buttonColor: colorField("Button Color"),
    buttonTextColor: colorField("Button Text Color"),
    paddingY: { type: "number", label: "Vertical Padding", min: 40, max: 120 },
    apiEndpoint: {
      type: "text",
      label: "API Endpoint (optional)",
    },
  },
  defaultProps: {
    heading: "Subscribe to Our Newsletter",
    subheading: "Stay updated with our latest events and news",
    buttonText: "Subscribe",
    placeholderText: "Enter your email",
    backgroundColor: "#17265a",
    textColor: "#ffffff",
    buttonColor: "#ffffff",
    buttonTextColor: "#17265a",
    paddingY: 80,
    apiEndpoint: "",
  },
  render: (props: any) => {
    const NewsletterSectionComponent: React.FC = () => {
      const {
        heading,
        subheading,
        buttonText,
        placeholderText,
        backgroundColor,
        textColor,
        buttonColor,
        buttonTextColor,
        paddingY,
        apiEndpoint,
      } = props;
      const [email, setEmail] = React.useState("");
      const [status, setStatus] = React.useState<
        "idle" | "loading" | "success" | "error"
      >("idle");
      const [message, setMessage] = React.useState("");

      const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !email.includes("@")) {
          setStatus("error");
          setMessage("Please enter a valid email address");
          return;
        }

        setStatus("loading");
        setMessage("");

        try {
          if (apiEndpoint) {
            // If API endpoint is provided, use it
            const response = await fetch(apiEndpoint, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email }),
            });

            if (response.ok) {
              setStatus("success");
              setMessage("Thank you for subscribing!");
              setEmail("");
            } else {
              throw new Error("Subscription failed");
            }
          } else {
            // Default behavior - just show success (you can integrate with your API later)
            setTimeout(() => {
              setStatus("success");
              setMessage("Thank you for subscribing!");
              setEmail("");
            }, 500);
          }
        } catch (error) {
          setStatus("error");
          setMessage("Something went wrong. Please try again.");
        }
      };

      return (
        <section
          style={{
            backgroundColor,
            color: textColor,
            padding: `${paddingY}px 24px`,
          }}
        >
          <div
            style={{ maxWidth: "800px", margin: "0 auto", textAlign: "center" }}
          >
            {heading && (
              <h2
                style={{
                  fontSize: "clamp(28px, 5vw, 42px)",
                  fontWeight: 700,
                  marginBottom: "16px",
                  color: textColor,
                }}
              >
                {heading}
              </h2>
            )}
            {subheading && (
              <p
                style={{
                  fontSize: "18px",
                  opacity: 0.9,
                  marginBottom: "32px",
                }}
              >
                {subheading}
              </p>
            )}

            <form
              onSubmit={handleSubmit}
              style={{
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={placeholderText}
                required
                style={{
                  flex: "1 1 300px",
                  minWidth: "250px",
                  padding: "14px 20px",
                  borderRadius: "8px",
                  border: "none",
                  fontSize: "16px",
                  backgroundColor: "#ffffff",
                  color: "#000000",
                }}
              />
              <button
                type="submit"
                disabled={status === "loading"}
                style={{
                  padding: "14px 32px",
                  backgroundColor: buttonColor,
                  color: buttonTextColor,
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: 600,
                  cursor: status === "loading" ? "not-allowed" : "pointer",
                  opacity: status === "loading" ? 0.7 : 1,
                  transition: "opacity 0.2s",
                }}
              >
                {status === "loading" ? "Subscribing..." : buttonText}
              </button>
            </form>

            {message && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  borderRadius: "6px",
                  backgroundColor: status === "success" ? "#10b981" : "#ef4444",
                  color: "#ffffff",
                  fontSize: "14px",
                }}
              >
                {message}
              </div>
            )}
          </div>
        </section>
      );
    };
    return <NewsletterSectionComponent />;
  },
};
